import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import echo from '../services/echo';
import LogsFilterPanel from '../components/logs/LogsFilterPanel';
import LogsTable from '../components/logs/LogsTable';
import LogsStats from '../components/logs/LogsStats';
import LogsChart from '../components/logs/LogsChart';
import ProtectedPage from '../components/common/ProtectedPage';

function todayYmd() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function weekAgoYmd() {
  const now = new Date();
  const weekAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000)); // 30 dias atrás
  const y = weekAgo.getFullYear();
  const m = String(weekAgo.getMonth() + 1).padStart(2, "0");
  const d = String(weekAgo.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default function LogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total: 0,
    per_page: 50,
    last_page: 1
  });

  // Filtros
  const [filters, setFilters] = useState({
    dateStart: weekAgoYmd(),
    dateEnd: todayYmd(),
    userId: '',
    action: '',
    entityType: '',
    ipAddress: '',
    searchTerm: '',
    showErrors: false,
    showCritical: false
  });

  // Estado de atualização em tempo real
  const [realTimeEnabled, setRealTimeEnabled] = useState(true);
  const [newLogsCount, setNewLogsCount] = useState(0);

  // Estado para dashboards
  const [stats, setStats] = useState({
    total: 0,
    errors: 0,
    critical: 0,
    users: 0,
    sessions: 0
  });

  const [chartData, setChartData] = useState({
    timeline: [],
    actions: [],
    users: []
  });
  const [filterOptions, setFilterOptions] = useState({
    actions: [],
    entity_types: []
  });

  // Carregar logs
  const loadLogs = async (page = 1, resetList = true) => {
    try {
      setLoading(true);
      
      const params = {
        page,
        per_page: pagination.per_page,
        date_start: filters.dateStart,
        date_end: filters.dateEnd,
        user_id: filters.userId || undefined,
        action: filters.action || undefined,
        entity_type: filters.entityType || undefined,
        ip_address: filters.ipAddress || undefined,
        search: filters.searchTerm || undefined,
        errors_only: filters.showErrors || undefined,
        critical_only: filters.showCritical || undefined
      };

      // Remove parâmetros undefined
      Object.keys(params).forEach(key => 
        params[key] === undefined && delete params[key]
      );

      const response = await api.get('/admin/logs', { params });
      const data = response.data;

      if (resetList) {
        setLogs(data.data || []);
      } else {
        setLogs(prev => [...prev, ...(data.data || [])]);
      }

      setPagination({
        current_page: data.current_page || 1,
        total: data.total || 0,
        per_page: data.per_page || 50,
        last_page: data.last_page || 1
      });

      setStats(data.stats || stats);
      setChartData(data.charts || chartData);
      setFilterOptions(data.filter_options || { actions: [], entity_types: [] });
      setNewLogsCount(0);

    } catch (error) {
      console.error('Erro ao carregar logs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Carregar mais logs (paginação infinita)
  const loadMoreLogs = () => {
    if (pagination.current_page < pagination.last_page && !loading) {
      loadLogs(pagination.current_page + 1, false);
    }
  };

  // Aplicar filtros
  const applyFilters = () => {
    loadLogs(1, true);
  };

  // Limpar filtros
  const clearFilters = () => {
    setFilters({
      dateStart: weekAgoYmd(), // 30 dias atrás
      dateEnd: todayYmd(),
      userId: '',
      action: '',
      entityType: '',
      ipAddress: '',
      searchTerm: '',
      showErrors: false,
      showCritical: false
    });
  };

  // Carregar dados na inicialização
  useEffect(() => {
    loadLogs();
  }, []);

  // Setup do Echo para tempo real
  useEffect(() => {
    if (!realTimeEnabled) return;

    const channel = echo.private('admin.logs');
    
    const handleNewLog = (event) => {
      const newLog = event.log;
      
      // Increment counter if not showing the data
      if (pagination.current_page > 1) {
        setNewLogsCount(prev => prev + 1);
        return;
      }

      // Add to beginning if on first page (dedupe defensivo por id)
      setLogs(prev => {
        const withoutDuplicated = prev.filter((item) => item.id !== newLog.id);
        return [newLog, ...withoutDuplicated.slice(0, pagination.per_page - 1)];
      });
      
      // Update stats
      setStats(prev => ({
        ...prev,
        total: prev.total + 1,
        errors: prev.errors + (newLog.is_error ? 1 : 0),
        critical: prev.critical + (newLog.is_critical ? 1 : 0)
      }));
    };

    channel.listen('.log.created', handleNewLog);

    return () => {
      channel.stopListening('.log.created', handleNewLog);
    };
  }, [realTimeEnabled, pagination.current_page, pagination.per_page]);

  // Refresh data when new logs arrive
  const refreshData = () => {
    loadLogs(1, true);
  };

  // Memoized filtered logs for search
  const filteredLogs = useMemo(() => {
    if (!filters.searchTerm) return logs;
    
    const term = filters.searchTerm.toLowerCase();
    return logs.filter(log => 
      log.action_name?.toLowerCase().includes(term) ||
      log.user?.name?.toLowerCase().includes(term) ||
      (log.entity_type || log.entity?.type || '').toLowerCase().includes(term) ||
      log.ip_address?.includes(term)
    );
  }, [logs, filters.searchTerm]);

  return (
    <ProtectedPage permission="admin.logs" action="visualizar logs do sistema">
      <div className="logs-page">
      <div className="content-header">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="content-title">Sistema de Logs e Auditoria</h1>
            <p className="content-subtitle">
              Monitoramento de atividades em tempo real
            </p>
          </div>
        
          <div className="d-flex gap-2 align-items-center">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                checked={realTimeEnabled}
                onChange={(e) => setRealTimeEnabled(e.target.checked)}
                id="realtimeSwitch"
              />
              <label className="form-check-label text-light" htmlFor="realtimeSwitch">
                <i className="bi bi-broadcast me-1"></i>
                Tempo Real
              </label>
            </div>
            
            {newLogsCount > 0 && (
              <button 
                className="btn btn-success btn-sm"
                onClick={refreshData}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                {newLogsCount} novos logs
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <LogsStats stats={stats} />

      {/* Charts Dashboard */}
      <div className="row mb-4">
        <div className="col-12">
          <LogsChart data={chartData} />
        </div>
      </div>

      {/* Filter Panel */}
      <LogsFilterPanel
        filters={filters}
        onFiltersChange={setFilters}
        onApplyFilters={applyFilters}
        onClearFilters={clearFilters}
        loading={loading}
        filterOptions={filterOptions}
      />

      {/* Logs Table */}
      <LogsTable
        logs={filteredLogs}
        loading={loading}
        pagination={pagination}
        onLoadMore={loadMoreLogs}
        onRefresh={refreshData}
      />
      </div>

      <style>{`
        .logs-page {
          width: 100%;
          max-width: 100%;
        }

        .content-header {
          padding: 0.75rem 0.5rem;
        }
        
        .content-title {
          font-size: 2rem;
          font-weight: bold;
          color: #84a98c;
          margin: 0;
        }
        
        .content-subtitle {
          color: rgba(255, 255, 255, 0.7);
          margin: 0;
          font-size: 0.9rem;
        }
        
        .form-check-input:checked {
          background-color: #84a98c;
          border-color: #84a98c;
        }
        
        .btn-success {
          background-color: #84a98c;
          border-color: #84a98c;
        }
        
        .btn-success:hover {
          background-color: #6f8770;
          border-color: #6f8770;
        }

        @media (min-width: 1200px) {
          .logs-page .row {
            --bs-gutter-x: 0.9rem;
          }
        }
      `}</style>
    </ProtectedPage>
  );
}
